import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { LayoutPreviewCanvas, type LayoutRect } from '../shared/layout-preview-canvas';
import { useGetHtmlComponentsQuery } from '@/state/queries/html-components/use-get-html-components';
import type {
  CompositeComponentDto,
  AllowedInContext,
  CompositeRole,
} from '@/state/queries/composite-components/use-get-composite-components';
import type { CreateCompositeComponentDto } from '@/state/mutations/composite-components/use-create-composite-component';
import { FLOW_PLAYLIST_SCOPES } from '@/types/dtos/media/flow-playlist-scope.enum';
import { SESSION_TYPE } from '@/types/dtos/sessions/session-type.enum';

const CANONICAL_INFO_HIERARCHY_HINT: Record<string, string> = {
  'event-info': 'Used as the event-level automatic info fallback (after flow/session/break when those apply).',
  'flow-info': 'Used as the flow-level automatic info fallback.',
  'session-info': 'Used as the session-level automatic info fallback.',
  'break-info': 'Used as the break-level automatic info fallback.',
};

type CompositeApplicabilityForm = {
  flowScopes: string[];
  sessionTypes: string[];
  slotContentTypes: string[];
};

const SORT_ORDER_SCOPE_KEYS = [
  'pre_flow',
  'flow_intro',
  'flow_transition',
  'flow_resume',
  'flow_outro',
  'after_flow',
  'session',
  'slot:Break',
  'slot:Session',
] as const;

interface CompositeComponentRef {
  type: 'html';
  ref: string;
  position: { x: number; y: number; w: number; h: number };
  /** Optional: iterate over array at payload path (e.g. flow.agenda). HTML uses slot.* placeholders. */
  arrayPath?: string;
}

interface CompositeComponentFormModalProps {
  open: boolean;
  onClose: () => void;
  component: CompositeComponentDto | null;
  onSave: (data: CreateCompositeComponentDto, componentId?: string) => Promise<void>;
  isSaving: boolean;
}

const defaultComp = (): CompositeComponentRef => ({
  type: 'html',
  ref: '',
  position: { x: 0, y: 0, w: 100, h: 100 },
  arrayPath: undefined,
});

export default function CompositeComponentFormModal({
  open,
  onClose,
  component,
  onSave,
  isSaving,
}: CompositeComponentFormModalProps) {
  const ALLOWED_IN_OPTIONS: AllowedInContext[] = ['event', 'flow', 'break', 'session'];
  const { data: htmlComponents } = useGetHtmlComponentsQuery();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [role, setRole] = useState<CompositeRole>('virtualMedia');
  const [components, setComponents] = useState<CompositeComponentRef[]>([defaultComp()]);
  const [allowedIn, setAllowedIn] = useState<AllowedInContext[]>([]);
  const [applicability, setApplicability] = useState<CompositeApplicabilityForm>({
    flowScopes: [],
    sessionTypes: [],
    slotContentTypes: [],
  });
  const [sortOrderByScope, setSortOrderByScope] = useState<Record<string, number>>({});
  const [previewRatio, setPreviewRatio] = useState<'16:9' | '9:16'>('16:9');

  useEffect(() => {
    queueMicrotask(() => {
      if (component) {
        setName(component.name);
        setSlug(component.slug);
        setRole(component.role ?? 'virtualMedia');
        const raw = (component.definition?.components ?? []) as CompositeComponentRef[];
        setComponents(
          raw.filter((c) => c.type === 'html').length > 0 ? raw.filter((c) => c.type === 'html') : [defaultComp()],
        );
        setAllowedIn((component.allowedIn as AllowedInContext[]) ?? []);
        const app = component.applicability;
        setApplicability({
          flowScopes: (app?.flowScopes as string[]) ?? [],
          sessionTypes: (app?.sessionTypes as string[]) ?? [],
          slotContentTypes: (app?.slotContentTypes as string[]) ?? [],
        });
        setSortOrderByScope(component.sortOrderByScope ?? {});
      } else {
        setName('');
        setSlug('');
        setRole('virtualMedia');
        setComponents([defaultComp()]);
        setAllowedIn([]);
        setApplicability({ flowScopes: [], sessionTypes: [], slotContentTypes: [] });
        setSortOrderByScope({});
      }
    });
  }, [component, open]);

  const addComponent = () => {
    setComponents([...components, defaultComp()]);
  };

  const removeComponent = (index: number) => {
    if (components.length <= 1) return;
    setComponents(components.filter((_, i) => i !== index));
  };

  const updateComponent = (index: number, updates: Partial<CompositeComponentRef>) => {
    const next = [...components];
    next[index] = { ...next[index], ...updates };
    setComponents(next);
  };

  const updatePosition = (index: number, field: 'x' | 'y' | 'w' | 'h', value: number) => {
    const next = [...components];
    next[index] = { ...next[index], position: { ...next[index].position, [field]: value } };
    setComponents(next);
  };

  const trimmedName = name.trim();
  const trimmedSlug = slug.trim();
  const infoHierarchyHint = CANONICAL_INFO_HIERARCHY_HINT[trimmedSlug];

  const previewRects: LayoutRect[] = components.map((c, i) => {
    const label =
      c.type === 'html' && htmlComponents
        ? (htmlComponents.find((h) => h.id === c.ref)?.name ?? `HTML ${i + 1}`)
        : c.type;
    return { ...c.position, label };
  });

  const handleSubmit = async () => {
    const validComponents = components.filter((c) => c.ref);
    if (validComponents.length === 0) return;

    const componentsToSave = validComponents.map((c) => {
      const base = { type: c.type as 'html', ref: c.ref, position: c.position };
      return c.arrayPath?.trim() ? { ...base, arrayPath: c.arrayPath.trim() } : base;
    });

    const applicabilityPayload =
      applicability.flowScopes.length || applicability.sessionTypes.length || applicability.slotContentTypes.length
        ? {
            flowScopes: applicability.flowScopes.length ? applicability.flowScopes : undefined,
            sessionTypes: applicability.sessionTypes.length ? applicability.sessionTypes : undefined,
            slotContentTypes: applicability.slotContentTypes.length ? applicability.slotContentTypes : undefined,
          }
        : undefined;

    const data: CreateCompositeComponentDto = {
      name: trimmedName,
      slug: trimmedSlug,
      definition: {
        version: 1,
        components: componentsToSave,
      },
      scope: 'global',
      role,
      allowedIn: allowedIn.length > 0 ? allowedIn : component ? [] : undefined,
      applicability: component ? (applicabilityPayload ?? {}) : applicabilityPayload,
      sortOrderByScope: component
        ? sortOrderByScope
        : Object.keys(sortOrderByScope).length
          ? sortOrderByScope
          : undefined,
    };
    await onSave(data, component?.id);
    onClose();
  };

  const isValid = trimmedName && trimmedSlug && /^[a-z0-9-]+$/.test(trimmedSlug) && components.some((c) => c.ref);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{component ? 'Edit Composite Component' : 'Create Composite Component'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
            <TextField
              label="Slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="my-composite"
              fullWidth
              required
              disabled={!!component}
              helperText={
                infoHierarchyHint
                  ? `Lowercase, hyphens only. ${infoHierarchyHint}`
                  : 'Lowercase, hyphens only. Canonical info slugs: event-info, flow-info, session-info, break-info (different levels may coexist).'
              }
            />
          </Stack>

          <FormControl size="small" fullWidth>
            <InputLabel>Role</InputLabel>
            <Select value={role} label="Role" onChange={(e) => setRole(e.target.value as CompositeRole)}>
              <MenuItem value="virtualMedia">Virtual media (cued in playlists, fills the media slot)</MenuItem>
              <MenuItem value="infoOverlay">Info overlay (placed on slide templates by slug)</MenuItem>
            </Select>
          </FormControl>

          <FormControl component="fieldset">
            <InputLabel shrink sx={{ position: 'relative' }}>
              {role === 'infoOverlay' ? 'Allowed in (overlay contexts)' : 'Allowed in (media list contexts)'}
            </InputLabel>
            <FormGroup row sx={{ mt: 1, gap: 1 }}>
              {ALLOWED_IN_OPTIONS.map((ctx) => (
                <FormControlLabel
                  key={ctx}
                  control={
                    <Checkbox
                      checked={allowedIn.includes(ctx)}
                      onChange={(e) =>
                        setAllowedIn((prev) => (e.target.checked ? [...prev, ctx] : prev.filter((c) => c !== ctx)))
                      }
                    />
                  }
                  label={ctx}
                />
              ))}
            </FormGroup>
          </FormControl>

          <FormControl component="fieldset">
            <InputLabel shrink sx={{ position: 'relative' }}>
              Applicability — flow scopes
            </InputLabel>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              None selected = all flow scopes
            </Typography>
            <FormGroup row sx={{ mt: 1, gap: 1, flexWrap: 'wrap' }}>
              {FLOW_PLAYLIST_SCOPES.map((scope) => (
                <FormControlLabel
                  key={scope}
                  control={
                    <Checkbox
                      checked={applicability.flowScopes.includes(scope)}
                      onChange={(e) =>
                        setApplicability((prev) => ({
                          ...prev,
                          flowScopes: e.target.checked
                            ? [...prev.flowScopes, scope]
                            : prev.flowScopes.filter((s) => s !== scope),
                        }))
                      }
                    />
                  }
                  label={scope}
                />
              ))}
            </FormGroup>
          </FormControl>

          <FormControl component="fieldset">
            <InputLabel shrink sx={{ position: 'relative' }}>
              Applicability — session types
            </InputLabel>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              None selected = all session types
            </Typography>
            <FormGroup row sx={{ mt: 1, gap: 1, flexWrap: 'wrap' }}>
              {Object.values(SESSION_TYPE).map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={applicability.sessionTypes.includes(type)}
                      onChange={(e) =>
                        setApplicability((prev) => ({
                          ...prev,
                          sessionTypes: e.target.checked
                            ? [...prev.sessionTypes, type]
                            : prev.sessionTypes.filter((s) => s !== type),
                        }))
                      }
                    />
                  }
                  label={type}
                />
              ))}
            </FormGroup>
          </FormControl>

          <FormControl component="fieldset">
            <InputLabel shrink sx={{ position: 'relative' }}>
              Applicability — slot content types
            </InputLabel>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              None selected = all slot content types
            </Typography>
            <FormGroup row sx={{ mt: 1, gap: 1 }}>
              {['Break', 'Session'].map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={applicability.slotContentTypes.includes(type)}
                      onChange={(e) =>
                        setApplicability((prev) => ({
                          ...prev,
                          slotContentTypes: e.target.checked
                            ? [...prev.slotContentTypes, type]
                            : prev.slotContentTypes.filter((s) => s !== type),
                        }))
                      }
                    />
                  }
                  label={type}
                />
              ))}
            </FormGroup>
          </FormControl>

          <Box>
            <Box component="span" sx={{ typography: 'subtitle2', display: 'block', mb: 1 }}>
              Sort order by scope (lower = earlier)
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {SORT_ORDER_SCOPE_KEYS.map((key) => (
                <TextField
                  key={key}
                  label={key}
                  type="number"
                  size="small"
                  value={sortOrderByScope[key] ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setSortOrderByScope((prev) => {
                      const next = { ...prev };
                      if (raw === '') {
                        delete next[key];
                      } else {
                        next[key] = parseInt(raw, 10) || 0;
                      }
                      return next;
                    });
                  }}
                  sx={{ width: 120 }}
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box component="span" sx={{ typography: 'subtitle2' }}>
                Components
              </Box>
              <Button size="small" startIcon={<Add />} onClick={addComponent}>
                Add
              </Button>
            </Box>
            <Stack spacing={1} sx={{ maxHeight: 240, overflow: 'auto' }}>
              {components.map((comp, i) => (
                <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }} key={i}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>HTML component</InputLabel>
                      <Select
                        value={comp.ref}
                        label="HTML component"
                        onChange={(e) => updateComponent(i, { ref: e.target.value })}
                      >
                        <MenuItem value="">Select...</MenuItem>
                        {(htmlComponents ?? []).map((h) => (
                          <MenuItem key={h.id} value={h.id}>
                            {h.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="X"
                      type="number"
                      size="small"
                      value={comp.position.x}
                      onChange={(e) => updatePosition(i, 'x', parseFloat(e.target.value) || 0)}
                      sx={{ width: 55 }}
                      inputProps={{ min: 0, max: 100 }}
                    />
                    <TextField
                      label="Y"
                      type="number"
                      size="small"
                      value={comp.position.y}
                      onChange={(e) => updatePosition(i, 'y', parseFloat(e.target.value) || 0)}
                      sx={{ width: 55 }}
                      inputProps={{ min: 0, max: 100 }}
                    />
                    <TextField
                      label="W"
                      type="number"
                      size="small"
                      value={comp.position.w}
                      onChange={(e) => updatePosition(i, 'w', parseFloat(e.target.value) || 0)}
                      sx={{ width: 55 }}
                      inputProps={{ min: 0, max: 100 }}
                    />
                    <TextField
                      label="H"
                      type="number"
                      size="small"
                      value={comp.position.h}
                      onChange={(e) => updatePosition(i, 'h', parseFloat(e.target.value) || 0)}
                      sx={{ width: 55 }}
                      inputProps={{ min: 0, max: 100 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeComponent(i)}
                      color="error"
                      disabled={components.length <= 1}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                  <TextField
                    label="Array path (optional)"
                    size="small"
                    value={comp.arrayPath ?? ''}
                    onChange={(e) => updateComponent(i, { arrayPath: e.target.value || undefined })}
                    placeholder="e.g. flow.agenda"
                    helperText="Iterate over payload array. HTML uses slot.startTime, slot.title, slot.contributions"
                    fullWidth
                    sx={{ mt: 1 }}
                  />
                </Box>
              ))}
            </Stack>
          </Box>

          <LayoutPreviewCanvas rects={previewRects} aspectRatio={previewRatio} onAspectRatioChange={setPreviewRatio} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!isValid || isSaving}>
          {component ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
